import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
    useCallback,
    ReactNode,
} from "react";
import axios from "axios";
import { getUrl } from "@/utils/queries/utils/utils";

interface ExchangeRates {
    base: string;
    date: string;
    rates: {
        USD: number;
        GBP: number;
        CAD: number;
        AUD: number;
        EUR: number;
    };
}

interface ExchangeRatesContextType {
    rates: ExchangeRates | null;
    loading: boolean;
    error: Error | null;
    refreshRates: () => Promise<void>;
}

const ExchangeRatesContext = createContext<
    ExchangeRatesContextType | undefined
>(undefined);

const CACHE_KEY = "exchangeRates";
const CACHE_DATE_KEY = "exchangeRatesDate";
const CACHE_TTL = 60 * 60 * 1000; // 1 ora in millisecondi

/**
 * Provider per i tassi di cambio con cache singleton
 *
 * - Cache in localStorage con TTL di 1 ora
 * - Carica i tassi una volta all'avvio
 * - Condivide i tassi tra tutti i componenti
 * - Non fa fetch se cache è valida
 */
export const ExchangeRatesProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [rates, setRates] = useState<ExchangeRates | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    /**
     * Carica i tassi dal backend e aggiorna la cache
     */
    const fetchExchangeRates = useCallback(async (): Promise<void> => {
        try {
            setError(null);

            const { data } = await axios.get(
                getUrl(`${process.env.NEXT_PUBLIC_BASE_URL}`, "/exchange-rates")
            );

            if (data && data.rates) {
                setRates(data);
                // Salva in cache
                localStorage.setItem(CACHE_KEY, JSON.stringify(data));
                localStorage.setItem(CACHE_DATE_KEY, new Date().toISOString());
            }
        } catch (err) {
            // Rileva errori CORS specifici
            const isCorsError =
                err instanceof Error &&
                (err.message.includes("CORS") ||
                    err.message.includes("Access-Control") ||
                    (axios.isAxiosError(err) && err.code === "ERR_NETWORK"));

            if (isCorsError) {
                // eslint-disable-next-line no-console
                console.error(
                    "❌ CORS Error: Il backend non permette l'header X-Currency. " +
                        "Contatta il backend per aggiungere 'X-Currency' (o 'x-currency') " +
                        "agli header permessi nella configurazione CORS.",
                    err
                );
                setError(
                    new Error(
                        "CORS Error: Backend deve permettere header X-Currency"
                    )
                );
            } else {
                // eslint-disable-next-line no-console
                console.error("Error fetching exchange rates:", err);
                setError(
                    err instanceof Error
                        ? err
                        : new Error("Failed to fetch exchange rates")
                );
            }
            throw err;
        }
    }, []);

    /**
     * Carica i tassi dalla cache (anche scaduta) se disponibili, altrimenti dal backend
     * Se il backend fallisce, usa sempre l'ultima cache disponibile
     */
    const loadCache = useCallback((): ExchangeRates | null => {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) {
            return null;
        }

        try {
            const cachedRates = JSON.parse(cached);
            // Verifica che la cache abbia la struttura corretta
            if (
                cachedRates &&
                cachedRates.rates &&
                typeof cachedRates.rates === "object"
            ) {
                return cachedRates;
            }
        } catch (parseError) {
            // eslint-disable-next-line no-console
            console.error("Error parsing cached exchange rates:", parseError);
        }
        return null;
    }, []);

    /**
     * Carica i tassi dalla cache se validi, altrimenti dal backend
     */
    const loadExchangeRates = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);

            // Controlla se c'è una cache valida (< 1 ora)
            const cached = localStorage.getItem(CACHE_KEY);
            const cachedDate = localStorage.getItem(CACHE_DATE_KEY);

            if (cached && cachedDate) {
                const cacheAge = Date.now() - new Date(cachedDate).getTime();

                // Se cache è valida (< 1 ora), usa quella e NON fare fetch
                if (cacheAge < CACHE_TTL) {
                    const cachedRates = loadCache();
                    if (cachedRates) {
                        setRates(cachedRates);
                        setLoading(false);
                        // Cache valida, non fare fetch al backend
                        return;
                    }
                }
            }

            // Cache non valida o assente, prova a fare fetch dal backend
            await fetchExchangeRates();
        } catch (err) {
            // Se fetch fallisce (es. backend in 500), usa SEMPRE l'ultima cache disponibile
            // anche se scaduta o molto vecchia
            const cachedRates = loadCache();
            if (cachedRates) {
                setRates(cachedRates);
                // eslint-disable-next-line no-console
                console.warn(
                    "Backend fetch failed, using last available cache (even if expired)"
                );
            } else {
                // Nessuna cache disponibile, i tassi rimangono null
                // La conversione userà i prezzi in EUR come fallback
                setError(
                    new Error("No exchange rates available and no cache found")
                );
                // eslint-disable-next-line no-console
                console.error(
                    "No cache available and backend fetch failed. Prices will remain in EUR."
                );
            }
        } finally {
            setLoading(false);
        }
    }, [fetchExchangeRates, loadCache]);

    /**
     * Forza il refresh dei tassi (ignora cache)
     */
    const refreshRates = useCallback(async (): Promise<void> => {
        await fetchExchangeRates();
    }, [fetchExchangeRates]);

    // Carica i tassi una volta all'avvio
    // Usa useRef per tracciare se abbiamo già caricato
    const hasLoadedRef = React.useRef(false);

    useEffect(() => {
        // Carica solo una volta all'avvio
        if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
            loadExchangeRates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Dipendenze vuote per eseguire solo al mount

    // Memoizza il value del Context per evitare re-render inutili
    const contextValue = useMemo(
        () => ({
            rates,
            loading,
            error,
            refreshRates,
        }),
        [rates, loading, error, refreshRates]
    );

    return (
        <ExchangeRatesContext.Provider value={contextValue}>
            {children}
        </ExchangeRatesContext.Provider>
    );
};

/**
 * Hook per accedere ai tassi di cambio dal Context
 *
 * Usa questo hook invece di useExchangeRates per evitare fetch multipli
 */
export const useExchangeRatesContext = (): ExchangeRatesContextType => {
    const context = useContext(ExchangeRatesContext);
    if (context === undefined) {
        throw new Error(
            "useExchangeRatesContext must be used within ExchangeRatesProvider"
        );
    }
    return context;
};
